import { getApiBaseUrl } from '@/services/api/base-url';
import { getAnalyticsMetaProperties } from '@/services/analytics/meta-properties';

/**
 * Fire a pre-auth login analytics event to the global `capture` endpoint.
 *
 * The login page runs before any workspace exists, so it can't use Hydra's
 * workspace-scoped `useAnalytics` (which posts to the environment-scoped
 * tracking endpoint). Instead we mirror what the Ember login did: post directly
 * to the global `capture` endpoint with the same event names, the standard
 * `getAnalyticsMetaProperties()` block every Hydra/Ember event carries, and the
 * per-event properties. Failures are swallowed — analytics is best-effort and
 * must never block or surface on the login form.
 *
 * NOTE: confirm the `capture` endpoint path with the backend — Ember resolved
 * it via its ApiService; here we target `${apiBase}/v1/capture`.
 */

/**
 * The login URL can carry a 2FA email-link `token` and `email` in its query
 * string, and `onNavigateStep` preserves those across step changes — so the raw
 * `url`/`search` meta properties would leak a still-valid login credential into
 * analytics. Strip them from both before reporting. (Ember sent the full href;
 * this closes that off.)
 */
function sanitizedLocation(): { url: string; search: string } {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  url.searchParams.delete('email');
  return { url: url.toString(), search: url.search };
}

export function trackLoginEvent(event: string, properties: Record<string, unknown> = {}): void {
  void fetch(`${getApiBaseUrl()}/v1/capture`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      event,
      properties: { ...getAnalyticsMetaProperties(), ...sanitizedLocation(), ...properties },
    }),
  }).catch(() => {
    // Analytics is best-effort; never block or surface login on a tracking failure.
  });
}

export const LoginAnalyticsEvent = {
  SignInWithDifferentEmailClicked: 'User: Sign in With Different Email Clicked',
  SignInWithGoogleClicked: 'User: Sign in With Google Clicked',
  ChooseUserListViewed: 'User: Choose User List Viewed',
  LoggedIntoNewAccount: 'Multi-Account: User Logged Into New Account',
} as const;
